import { inject, Injectable } from '@angular/core';
import { ThemeService } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LenderService } from '@shared/service/lender/lender.service';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * Initializer Lender data after getting lender config
 */
@Injectable({
  providedIn: 'root'
})
export class InitializeLenderService {
  private logger = inject(SawDust);
  private logContext(additional: Record<string, unknown> = {}) {
    return { component: 'InitializeLenderService', ...additional };
  }
  constructor(
    private readonly lenderService: LenderService,
    private readonly themeService: ThemeService,
    private readonly googleTrackingService: GoogleTrackingService,
    private readonly runtimeConfigService: RuntimeConfigService
  ) {}

  /**
   * Sets the lender-specific configurations such as theme settings and analytics.
   *
   * @param lenderConfig - Contains the lender's configuration such as theme colors, ADA level, and logo URL.
   */
  public setLenderConfigData(lenderConfig: LenderConfiguration): void {
    this.logger.debug('Initializing lender configuration', this.logContext({ lenderCode: lenderConfig?.consumerLoanCode }));
    try {
      // Get app config - note that we can capture the resulting config here if it's needed in this method
      const appConfig = this.runtimeConfigService.getConfig;

      // This is overridden elsewhere in the application, but it gives us a baseline
      this.lenderService.lenderConfigData = lenderConfig;
      this.themeService.setTheme(lenderConfig.mainColor, lenderConfig.adaLevel || 'aa', lenderConfig.lenderLogoUrl);

      // Set up **Lender Specific** Analytics and Origence GTM...
      // NOTE: The Origence DX GTM is set in index.html!
      this.googleTrackingService.initGoogleServices(lenderConfig, appConfig);

      this.logger.debug('Lender configuration initialized successfully', this.logContext({ lenderCode: lenderConfig?.consumerLoanCode }));

      // Capture errors in the console if the lender config data is not set
    } catch (error) {
      this.logger.error('Error setting lender config data', error, this.logContext({ lenderCode: lenderConfig?.consumerLoanCode }));
    }
  }
}
